// @core/stores/calendar/CalendarEventStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { calendarApi } from '@core/api';
import type {
  CalendarEvent,
  CancelEventRequest,
  CreateEventFromTemplateRequest,
  CreateEventRequest,
  CreateRecurringEventRequest,
  DuplicateEventRequest,
  EventResponse,
  EventSeriesResponse,
  InviteAttendeesRequest,
  ListEventsResponse,
  MoveEventRequest,
  ProposeNewTimeRequest,
  RespondToInvitationRequest,
  RestoreEventRequest,
  UpdateEventRequest,
  UpdateRecurringEventRequest,
} from '@core/types/calendar';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class CalendarEventStore {
  // List state
  items: CalendarEvent[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: CalendarEvent | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, CalendarEvent>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): CalendarEvent | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: CalendarEvent | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel event
   */
  async CancelEvent(event_id: string, data: CancelEventRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.CancelEvent(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create event
   */
  async CreateEvent(calendar_id: string, data: CreateEventRequest): Promise<CalendarEvent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.CreateEvent(calendar_id, data);
      const item = response.event;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create event from template
   */
  async CreateEventFromTemplate(template_id: string, data: CreateEventFromTemplateRequest): Promise<CalendarEvent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.CreateEventFromTemplate(template_id, data);
      const item = response.event;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create recurring event
   */
  async CreateRecurringEvent(calendar_id: string, data: CreateRecurringEventRequest): Promise<EventSeriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.CreateRecurringEvent(calendar_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete event
   */
  async DeleteEvent(event_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await calendarApi.DeleteEvent(event_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== event_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === event_id) {
          this.currentItem = null;
        }

        this.removeFromCache(event_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete recurring event occurrence
   */
  async DeleteRecurringEventOccurrence(event_id: string, original_start: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await calendarApi.DeleteRecurringEventOccurrence(event_id, original_start);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== original_start);

        // Clear current item if same
        if (this.currentItem?.entity?.id === original_start) {
          this.currentItem = null;
        }

        this.removeFromCache(original_start);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Duplicate event
   */
  async DuplicateEvent(event_id: string, data: DuplicateEventRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.DuplicateEvent(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get event
   */
  async GetEvent(event_id: string): Promise<CalendarEvent> {
    // Check cache first
    const cached = this.cache.get(event_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await calendarApi.GetEvent(event_id);
      const item = response.event;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List recurring event instances
   */
  async GetRecurringEventInstances(event_id: string, params?: { time_max?: string | number | boolean; time_min?: string | number | boolean; }): Promise<ListEventsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.GetRecurringEventInstances(event_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Invite attendees
   */
  async InviteAttendees(event_id: string, data: InviteAttendeesRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.InviteAttendees(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List events
   */
  async ListEvents(calendar_id: string, params?: PaginatedRequest & { sort_by?: string | number | boolean; sort_desc?: string | number | boolean; time_max?: string | number | boolean; time_min?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await calendarApi.ListEvents(calendar_id, params);

      runInAction(() => {
        this.items = response.events?.events ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my events
   */
  async ListMyEvents(params?: PaginatedRequest & { time_max?: string | number | boolean; time_min?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await calendarApi.ListMyEvents(params);

      runInAction(() => {
        this.items = response.events?.events ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Move event
   */
  async MoveEvent(event_id: string, data: MoveEventRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.MoveEvent(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Propose new time
   */
  async ProposeNewTime(event_id: string, data: ProposeNewTimeRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ProposeNewTime(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove attendee
   */
  async RemoveAttendee(event_id: string, attendee_id: string): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.RemoveAttendee(event_id, attendee_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Respond to invitation
   */
  async RespondToInvitation(event_id: string, data: RespondToInvitationRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.RespondToInvitation(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Restore event
   */
  async RestoreEvent(event_id: string, data: RestoreEventRequest): Promise<EventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.RestoreEvent(event_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update event
   */
  async UpdateEvent(event_id: string, data: UpdateEventRequest): Promise<CalendarEvent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.UpdateEvent(event_id, data);
      const item = response.event;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update recurring event
   */
  async UpdateRecurringEvent(event_id: string, data: UpdateRecurringEventRequest): Promise<CalendarEvent> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.UpdateRecurringEvent(event_id, data);
      const item = response.event;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const calendarEventStore = new CalendarEventStore();
